import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@arcadeai/design-system/components/ui/atoms/alert";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@arcadeai/design-system/components/ui/atoms/collapsible";
import { Form } from "@arcadeai/design-system/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearch } from "@tanstack/react-router";
import { AlertCircle, ChevronDown, Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { generateExternalId } from "@/app/oauth/utils";
import { useServerAuthStatus } from "@/app/servers/api/hooks";
import { OAuthRequiredAlert } from "@/app/servers/components/oauth/oauth-required-alert";
import {
  createServerFormSchema,
  requiresOAuth,
  type Server,
  type ServerForm as ServerFormType,
} from "@/app/servers/types";
import { removeEmptyValues } from "@/lib/utils";
import { AdvancedSettingsCollapsible } from "./fields/advanced-settings-collapsible";
import { HeadersField } from "./fields/headers-field";
import { IdField } from "./fields/id-field";
import { OAuth2Fields } from "./fields/oauth2-fields";
import { RetryField } from "./fields/retry-field";
import { SecretField } from "./fields/secret-field";
import { TimeoutField } from "./fields/timeout-field";
import { TypeField } from "./fields/type-field";
import { UriField } from "./fields/uri-field";
import {
  cleanOAuth2Values,
  createDefaultFormValues,
  createFormValuesFromServer,
  DEFAULT_WORKER_CONFIG,
  hasAdvancedMCPConfig,
} from "./utils";

type ServerFormComponentProps = {
  server?: Server;
  onSubmit: (values: ServerFormType) => Promise<void>;
  isLoading: boolean;
};

const ServerFormAlerts = ({
  server,
  canUpdate,
}: {
  server?: Server;
  canUpdate: boolean;
}) => {
  const needsOAuth = server ? requiresOAuth(server) : false;
  const { isAuthorized } = useServerAuthStatus(
    server?.id ?? "",
    server?.binding.type,
    server?.type
  );
  const showAuthWarning = needsOAuth && !isAuthorized;

  return (
    <>
      {server && !canUpdate && (
        <Alert className="mb-6 border-orange-500" variant="warning">
          <AlertCircle className="size-4" />
          <AlertTitle>Static Server</AlertTitle>
          <AlertDescription className="mt-2">
            These settings are locked for editing and can only be updated
            through your configuration file.
          </AlertDescription>
        </Alert>
      )}
      {!!(server && showAuthWarning) && <OAuthRequiredAlert server={server} />}
    </>
  );
};

const MCPAdvancedSettings = ({
  isOpen,
  onOpenChange,
  canUpdate,
  form,
  server,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  canUpdate: boolean;
  form: ReturnType<typeof useForm<ServerFormType>>;
  server?: Server;
}) => (
  <Collapsible onOpenChange={onOpenChange} open={isOpen}>
    <div className="rounded-lg border border-zinc-600">
      <CollapsibleTrigger
        render={
          <button
            className="flex w-full cursor-pointer items-center justify-between rounded-lg bg-transparent p-4 transition-colors hover:bg-zinc-800/50"
            type="button"
          />
        }
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">Advanced Settings</span>
          <span className="text-muted-foreground text-xs">
            Connection Settings, OAuth & Headers
          </span>
        </div>
        <ChevronDown
          className={`size-4 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-zinc-600 border-t p-4">
          <div className="space-y-4">
            <div>
              <h4 className="mb-3 font-medium text-sm">Connection Settings</h4>
              <div className="space-y-4">
                <TimeoutField canUpdate={canUpdate} form={form} type="mcp" />
                <RetryField canUpdate={canUpdate} form={form} type="mcp" />
              </div>
            </div>
            <div className="my-6 border-zinc-700 border-t" />
            <div>
              <h4 className="mb-3 font-medium text-sm">OAuth Configuration</h4>
              <OAuth2Fields canUpdate={canUpdate} form={form} server={server} />
            </div>
            <div className="my-6 border-zinc-700 border-t" />
            <div>
              <h4 className="mb-3 font-medium text-sm">Custom Headers</h4>
              <HeadersField canUpdate={canUpdate} form={form} server={server} />
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </div>
  </Collapsible>
);

export const ServerForm = ({
  server,
  onSubmit,
  isLoading,
}: ServerFormComponentProps) => {
  const { tab, defaultValues } = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/servers/",
  });

  const isNew = tab === "new";
  const defaultFormValues: ServerFormType = isNew
    ? createDefaultFormValues(defaultValues)
    : createDefaultFormValues();

  const form = useForm<ServerFormType>({
    resolver: zodResolver(createServerFormSchema(server)),
    defaultValues: server
      ? createFormValuesFromServer(server)
      : defaultFormValues,
  });

  const [isAdvancedOpen, setIsAdvancedOpen] = useState(() =>
    hasAdvancedMCPConfig(server)
  );
  const [isHttpAdvancedOpen, setIsHttpAdvancedOpen] = useState(false);

  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  const serverType = (form.watch("type") ?? "http") as "http" | "mcp";
  const canUpdate =
    !server || Boolean(server && server.binding.type !== "static");

  const handleTypeChange = useCallback(
    (type: "http" | "mcp") => {
      form.setValue("type", type);
      if (type === "http") {
        form.setValue("mcp", undefined);
        form.setValue("http", {
          ...DEFAULT_WORKER_CONFIG,
          uri: "",
          secret: "",
        });
      } else {
        form.setValue("http", undefined);
        form.setValue("mcp", {
          ...DEFAULT_WORKER_CONFIG,
          uri: "",
          oauth2: { external_id: generateExternalId() },
        });
      }
    },
    [form]
  );

  const handleSubmit = useCallback(
    async (values: ServerFormType) => {
      const cleanedValues = cleanOAuth2Values(removeEmptyValues(values));
      // Preserve mcp.headers and mcp.secrets after cleaning so the API
      // receives explicit empty objects when all entries have been deleted.
      // Without this, removeEmptyValues strips {} and "", the PATCH omits
      // the fields entirely, and the backend keeps stale entries.
      // The engine uses merge semantics for secrets: empty string = keep
      // existing value, non-empty = update, absent key = delete.
      if (cleanedValues.mcp && values.mcp?.headers !== undefined) {
        cleanedValues.mcp.headers = cleanedValues.mcp.headers ?? {};
      }
      if (cleanedValues.mcp && values.mcp?.secrets !== undefined) {
        cleanedValues.mcp.secrets = values.mcp.secrets ?? {};
      }
      // Preserve an explicit empty authorization_url so the engine receives
      // the signal to clear a previously-set value. Without this,
      // removeEmptyValues strips "" and the PATCH omits the field, which the
      // engine interprets as "no change" and leaves the stale URL in place.
      // Only do this on edit — the create endpoint's validator rejects "",
      // and there is nothing to clear when no server exists yet.
      if (
        server &&
        cleanedValues.mcp &&
        values.mcp?.oauth2?.authorization_url === ""
      ) {
        cleanedValues.mcp.oauth2 = {
          ...cleanedValues.mcp.oauth2,
          authorization_url: "",
        };
      }
      await onSubmit(cleanedValues);
    },
    [onSubmit, server]
  );

  return (
    <>
      <ServerFormAlerts canUpdate={canUpdate} server={server} />
      <Form {...form}>
        <form
          className="flex flex-col gap-4"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          {!server && (
            <TypeField
              canUpdate={canUpdate}
              form={form}
              onTypeChange={handleTypeChange}
            />
          )}
          <div className="space-y-4 rounded-lg border border-zinc-600 p-4">
            <IdField canUpdate={!server} form={form} />
            <UriField
              canUpdate={canUpdate}
              form={form}
              isManaged={server?.managed}
              type={serverType}
            />
            {serverType === "http" && (
              <SecretField
                canUpdate={canUpdate}
                form={form}
                isNew={isNew}
                server={server}
              />
            )}
          </div>

          {serverType === "http" && (
            <AdvancedSettingsCollapsible
              canUpdate={canUpdate}
              form={form}
              isOpen={isHttpAdvancedOpen}
              onOpenChange={setIsHttpAdvancedOpen}
              type="http"
            />
          )}

          {serverType === "mcp" && (
            <MCPAdvancedSettings
              canUpdate={canUpdate}
              form={form}
              isOpen={isAdvancedOpen}
              onOpenChange={setIsAdvancedOpen}
              server={server}
            />
          )}
          {!!canUpdate && (
            <Button
              className="mt-2 mb-4 w-full"
              disabled={isLoading}
              type="submit"
            >
              {isLoading ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : null}
              <span>{server ? "Update Server" : "Add Server"}</span>
            </Button>
          )}
        </form>
      </Form>
    </>
  );
};
